import type {ReactElement} from 'react';
import type {CustomOutlineBoxPositionAndSize} from './custom-outline-boxes.types';
import {CUSTOM_HOVER_BOX_CLASS} from './custom-outline-boxes.types';
import {CUSTOM_MULTI_SELECT_OUTLINE_THICKNESS, CUSTOM_MULTI_SELECTION_BOXES_WRAPPER, CUSTOM_SELECTION_BOX_CLASS} from './custom-outline-boxes.types';
import styles from './custom-outline-boxes.module.scss';
import {useAppSelector} from '@/hooks';
import {degreesToRadians} from '@Utils/math.util';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {ItemBorderColor} from '@PosterWhiteboard/poster/poster-item-controls';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {useGetItemsFabricObjectPositionOrDimensionKeysMap} from '@Hooks/poster-editor/useGetItemsFabricObjectPositionOrDimensionKeysMap';
import type {FabricObjectPositionOrDimensionKeys} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {SelectionType} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {useShouldHideHoveredItemControlsWhenAnimating} from '@Hooks/poster-editor/useShouldHideControlsWhenItemAnimating';
import {useDrilledGroupItem} from '@Hooks/poster-editor/useDrilledGroupItem';

export function CustomOutlineBoxes(props: CustomItemControlsWrapperProps): ReactElement | null {
  const activeItemsPositionPropertiesMap = useGetItemsFabricObjectPositionOrDimensionKeysMap(props.pageHashedId);
  const hoveredItemPositionPropertiesMap = useGetItemsFabricObjectPositionOrDimensionKeysMap(props.pageHashedId, SelectionType.HOVERED_ITEM);
  const multiSelectionPositionPropertiesMap = useGetItemsFabricObjectPositionOrDimensionKeysMap(props.pageHashedId, SelectionType.MULTI_SELECTION);
  const marqueePreviewPositionPropertiesMap = useGetItemsFabricObjectPositionOrDimensionKeysMap(props.pageHashedId, SelectionType.MARQUEE_PREVIEW);

  let multiSelectionPositionProperties = undefined;
  if (multiSelectionPositionPropertiesMap && Object.values(multiSelectionPositionPropertiesMap)[0] !== undefined) {
    multiSelectionPositionProperties = Object.values(multiSelectionPositionPropertiesMap)[0];
  }

  const activeItemIds = useAppSelector((state) => {
    return state.posterEditor.activeItemIds[props.pageHashedId];
  });

  const shouldHideControls = useAppSelector((state) => {
    return state.posterEditor.shouldHideControls[activeItemIds ? activeItemIds[0] : ''];
  });

  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });

  const hoveredItemId = useAppSelector((state) => {
    return state.posterEditor.hoveredItemId[props.pageHashedId];
  });

  const marqueePreviewItemIds = useAppSelector((state) => {
    return state.posterEditor.marqueePreviewItemIds[props.pageHashedId];
  });

  const isMobileVariant = useAppSelector((state) => {
    return state.posterEditor.isMobileVariant;
  });

  const hideHoveredItemDueToAnimation = useShouldHideHoveredItemControlsWhenAnimating(props.pageHashedId);
  const drilledItem = useDrilledGroupItem(props.pageHashedId);

  if (shouldHideControls) {
    return null;
  }

  const getMultiSelectionOutlinePositionAndSize = (controlPositions: FabricObjectPositionOrDimensionKeys): CustomOutlineBoxPositionAndSize => {
    const scaledHeight = controlPositions.height * controlPositions.scaleY * controlPositions.posterScale;
    const scaledWidth = controlPositions.width * controlPositions.scaleX * controlPositions.posterScale;

    const offsetDueToBorderWidth = CUSTOM_MULTI_SELECT_OUTLINE_THICKNESS;

    const leftInitial = controlPositions.left * controlPositions.posterScale - scaledWidth / 2;
    const topInitial = controlPositions.top * controlPositions.posterScale - scaledHeight / 2;

    const xCorrectionDueToHeight = (scaledHeight / 2) * Math.sin(degreesToRadians(controlPositions.angle));
    const xCorrectionDueToWidth = (scaledWidth / 2) * Math.cos(degreesToRadians(controlPositions.angle));

    const yCorrectionDueToHeight = (scaledHeight / 2) * Math.cos(degreesToRadians(controlPositions.angle));
    const yCorrectionDueToWidth = (scaledWidth / 2) * Math.sin(degreesToRadians(controlPositions.angle));

    const height = controlPositions.strokeWidth * controlPositions.posterScale + scaledHeight + offsetDueToBorderWidth * 2;
    const width = controlPositions.strokeWidth * controlPositions.posterScale + scaledWidth + offsetDueToBorderWidth * 2;
    const left = leftInitial - xCorrectionDueToHeight + xCorrectionDueToWidth - offsetDueToBorderWidth - controlPositions.strokeWidth * controlPositions.posterScale;
    const top = topInitial + yCorrectionDueToHeight + yCorrectionDueToWidth - offsetDueToBorderWidth;

    return {angle: controlPositions.angle, height, width, left, top};
  };


  const getItemOutlinePositonAndSize = (item: ItemType, _page: Page, itemControlPositions: FabricObjectPositionOrDimensionKeys): CustomOutlineBoxPositionAndSize => {
    const itemFabricObject = item.fabricObject;
    const objectCenterX = itemFabricObject.getCenterPoint().x * itemControlPositions.posterScale;
    const objectCenterY = itemFabricObject.getCenterPoint().y * itemControlPositions.posterScale;

    let objectTotalScaledWidth: number;
    let objectTotalScaledHeight: number;

    if (multiSelectionPositionProperties === undefined) {
      objectTotalScaledWidth =
        itemControlPositions.width * itemControlPositions.scaleX * itemControlPositions.posterScale + itemControlPositions.strokeWidth * itemControlPositions.posterScale;
      objectTotalScaledHeight =
        itemControlPositions.height * itemControlPositions.scaleY * itemControlPositions.posterScale + itemControlPositions.strokeWidth * itemControlPositions.posterScale;
    } else {
      objectTotalScaledWidth =
        itemControlPositions.width * itemControlPositions.scaleX * multiSelectionPositionProperties.scaleX * itemControlPositions.posterScale +
        itemControlPositions.strokeWidth * itemControlPositions.posterScale;
      objectTotalScaledHeight =
        itemControlPositions.height * itemControlPositions.scaleY * multiSelectionPositionProperties.scaleY * itemControlPositions.posterScale +
        itemControlPositions.strokeWidth * itemControlPositions.posterScale;
    }

    const height = item.isLine() ? 0 : objectTotalScaledHeight + itemControlPositions.borderScaleFactor * 2;
    const width = objectTotalScaledWidth + itemControlPositions.borderScaleFactor * 2;
    const left = objectCenterX - objectTotalScaledWidth / 2 - itemControlPositions.borderScaleFactor;
    const top = item.isLine() ? objectCenterY - 1 : objectCenterY - objectTotalScaledHeight / 2 - itemControlPositions.borderScaleFactor;

    return {angle: itemFabricObject.getTotalAngle(), height, width, left, top};
  };

  const getMultiSelectionBoxesWrapper = (): ReactElement | null => {
    if (!activeItemIds || activeItemIds.length < 2) {
      return null;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection || !(activeSelection instanceof ActiveSelection)) {
      return null;
    }

    if (!multiSelectionPositionProperties) {
      return null;
    }

    const positionAndSize = getMultiSelectionOutlinePositionAndSize(multiSelectionPositionProperties);

    return (
      <div
        key={CUSTOM_MULTI_SELECTION_BOXES_WRAPPER}
        style={{
          visibility: 'visible',
          rotate: `${positionAndSize.angle}deg`,
          top: `${positionAndSize.top}px`,
          left: `${positionAndSize.left}px`,
          height: `${positionAndSize.height}px`,
          width: `${positionAndSize.width}px`,
          borderColor: ItemBorderColor.STATIC_ITEM,
        }}
        className={`${CUSTOM_SELECTION_BOX_CLASS} ${styles.multiSelectWrapper}`}
      />
    );
  };

  const getGroupHoverBox = (item: ItemType, page: Page, scale: number): ReactElement | null => {
    if (!item.groupId || drilledItem?.groupId === item.groupId) {
      return null;
    }
    const bbox = page.items.groups.getBoundingBoxForGroup(item.groupId);
    if (!bbox) {
      return null;
    }
    return (
      <div
        data-group-id={item.groupId}
        key={`group-hover-${item.groupId}`}
        style={{
          visibility: 'visible',
          top: `${bbox.top * scale}px`,
          left: `${bbox.left * scale}px`,
          width: `${bbox.width * scale}px`,
          height: `${bbox.height * scale}px`,
          rotate: `${bbox.angle}deg`,
          borderColor: ItemBorderColor.STATIC_ITEM,
        }}
        className={`${CUSTOM_HOVER_BOX_CLASS} ${styles.baseStyles}`}
      />
    );
  };

  const getHoverBox = (): ReactElement | null => {
    if (isMobileVariant || hideHoveredItemDueToAnimation || !hoveredItemId) {
      return null;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const item = page.items.getItem(hoveredItemId);
    if (!item) {
      return null;
    }

    if ((item.isLine() && arePosterItemsMoving) || activeItemIds?.includes(hoveredItemId)) {
      return null;
    }

    const positionProperties = hoveredItemPositionPropertiesMap?.[hoveredItemId];
    if (positionProperties === undefined) {
      return null;
    }

    const groupBox = getGroupHoverBox(item, page, positionProperties.posterScale);
    if (groupBox) {
      return groupBox;
    }

    const positionAndSize = getItemOutlinePositonAndSize(item, page, positionProperties);
    return (
      <div
        id={item.uid}
        key={hoveredItemId}
        style={{
          visibility: 'visible',
          rotate: `${positionAndSize.angle}deg`,
          top: `${positionAndSize.top}px`,
          left: `${positionAndSize.left}px`,
          height: `${positionAndSize.height}px`,
          width: `${positionAndSize.width}px`,
          borderColor: ItemBorderColor.STATIC_ITEM,
        }}
        className={`${CUSTOM_HOVER_BOX_CLASS} ${styles.baseStyles}  ${item.isLine() ? styles.line : ''}`}
      />
    );
  };

  const getPerItemSelectionBoxes = (): ReactElement | null => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    if (page.items.groups.isSelectionExactlyOneGroup()) {
      return null;
    }

    return (
      <>
        {activeItemIds?.map((selectedItemId) => {
          const item = page.items.getItem(selectedItemId);
          if (!item || item.isLine() || (item.isLine() && arePosterItemsMoving)) {
            return null;
          }

          if (activeItemsPositionPropertiesMap?.[selectedItemId] === undefined) {
            return null;
          }

          const positionAndSize = getItemOutlinePositonAndSize(item, page, activeItemsPositionPropertiesMap[selectedItemId]);

          return (
            <div
              id={item.uid}
              key={selectedItemId}
              style={{
                visibility: 'visible',
                top: `${positionAndSize.top}px`,
                left: `${positionAndSize.left}px`,
                width: `${positionAndSize.width}px`,
                height: `${positionAndSize.height}px`,
                rotate: `${positionAndSize.angle}deg`,
                borderColor: ItemBorderColor.STATIC_ITEM,
              }}
              className={`${CUSTOM_SELECTION_BOX_CLASS} ${styles.baseStyles}  ${item.isLine() ? styles.line : ''}`}
            />
          );
        })}
      </>
    );
  };

  const getDrillInGroupWrapper = (): ReactElement | null => {
    if (arePosterItemsMoving) {
      return null;
    }
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }
    if (!drilledItem) {
      return null;
    }
    if (!activeItemIds || activeItemIds.length !== 1) {
      return null;
    }
    const positionProperties = activeItemsPositionPropertiesMap?.[activeItemIds[0]];
    if (!positionProperties) {
      return null;
    }
    const bbox = page.items.groups.getBoundingBoxForGroup(drilledItem.groupId);
    if (!bbox) {
      return null;
    }
    const pad = positionProperties.borderScaleFactor;
    return (
      <div
        key={`drillin-wrapper-${drilledItem.groupId}`}
        style={{
          top: `${bbox.top * positionProperties.posterScale - pad}px`,
          left: `${bbox.left * positionProperties.posterScale - pad}px`,
          width: `${bbox.width * positionProperties.posterScale + pad * 2}px`,
          height: `${bbox.height * positionProperties.posterScale + pad * 2}px`,
          rotate: `${bbox.angle}deg`,
          borderColor: ItemBorderColor.STATIC_ITEM,
          borderStyle: 'dashed',
        }}
        className={styles.baseStyles}
      />
    );
  };

  /**
   * Hover-style boxes around every item the in-progress marquee box currently covers, so the
   * user can see what a mouseup would select. Grouped items show the whole group's hover box
   * (the selection is all-or-nothing per group) — once per group.
   */
  const getMarqueePreviewBoxes = (): ReactElement | null => {
    if (!marqueePreviewItemIds?.length) {
      return null;
    }
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }
    const previewBoxes: ReactElement[] = [];
    const renderedGroupIds = new Set<string>();
    for (const itemId of marqueePreviewItemIds) {
      const item = page.items.getItem(itemId);
      const positionProperties = marqueePreviewPositionPropertiesMap?.[itemId];
      if (!item || positionProperties === undefined) {
        continue;
      }
      if (item.groupId) {
        if (renderedGroupIds.has(item.groupId)) {
          continue;
        }
        const groupBox = getGroupHoverBox(item, page, positionProperties.posterScale);
        if (groupBox) {
          renderedGroupIds.add(item.groupId);
          previewBoxes.push(groupBox);
          continue;
        }
      }
      const positionAndSize = getItemOutlinePositonAndSize(item, page, positionProperties);
      previewBoxes.push(
        <div
          key={`marquee-preview-${itemId}`}
          style={{
            visibility: 'visible',
            rotate: `${positionAndSize.angle}deg`,
            top: `${positionAndSize.top}px`,
            left: `${positionAndSize.left}px`,
            height: `${positionAndSize.height}px`,
            width: `${positionAndSize.width}px`,
            borderColor: ItemBorderColor.STATIC_ITEM,
          }}
          className={`${CUSTOM_HOVER_BOX_CLASS} ${styles.baseStyles}  ${item.isLine() ? styles.line : ''}`}
        />
      );
    }
    return previewBoxes.length > 0 ? <>{previewBoxes}</> : null;
  };

  const getSelectionBoxesForSelectedItems = (): ReactElement => {
    return (
      <>
        {getMultiSelectionBoxesWrapper()}
        {getDrillInGroupWrapper()}
        {getHoverBox()}
        {getMarqueePreviewBoxes()}
        {getPerItemSelectionBoxes()}
      </>
    );
  };

  return getSelectionBoxesForSelectedItems();
}
